/**
 * localStorage that cannot take the app down.
 *
 * Touching window.localStorage THROWS rather than returning null in several
 * ordinary mobile configurations:
 *
 *   - Safari with "Block All Cookies" on — any access raises SecurityError.
 *     The setting blocks site data generally, not just cookies.
 *   - iOS Safari Private Browsing on older versions — setItem raises
 *     QuotaExceededError because the quota is zero.
 *   - Any browser with site data disabled for the origin, and some in-app
 *     webviews.
 *
 * Both call sites read it inside a useState initialiser, so the throw happened
 * during the very first render, before anything was painted and with no error
 * boundary above it — React unmounted the tree and the user got a white screen.
 * Desktop never hit it because storage is normally allowed there.
 *
 * A remembered dark-mode preference is a convenience. It must never be able to
 * stop the app from loading, so every access is wrapped and failure falls back
 * to the default.
 */

export function readStorage(key, fallback = null) {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

export function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
    return true;
  } catch {
    // Preference simply is not remembered this session. Not worth surfacing.
    return false;
  }
}
